import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Icon } from "@/lib/icons";
import { Download, X, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";

export function Certificate({ name, title, badgeIcon = "Award", badgeTitle, xp, date, onClose }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const prettyDate = date
    ? new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const download = async () => {
    if (!ref.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(ref.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#05060a" });
      const link = document.createElement("a");
      link.download = `ECO-Certificate-${(title || "Challenge").replace(/\s+/g, "-")}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Certificate downloaded");
    } catch (e) {
      toast.error("Could not generate certificate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200" data-testid="certificate-modal">
      <div className="w-full max-w-2xl">
        <div className="flex justify-end mb-3">
          <button onClick={onClose} data-testid="certificate-close" className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div
          ref={ref}
          style={{ background: "linear-gradient(135deg,#0a0e1a 0%,#05060a 55%,#0b0a06 100%)", border: "1px solid rgba(255,255,255,0.08)" }}
          className="rounded-2xl p-8 md:p-10 relative overflow-hidden"
          data-testid="certificate-card"
        >
          <div style={{ position: "absolute", top: -70, right: -70, width: 220, height: 220, background: "radial-gradient(circle,rgba(0,85,255,0.28),transparent 70%)" }} />
          <div style={{ position: "absolute", bottom: -70, left: -70, width: 220, height: 220, background: "radial-gradient(circle,rgba(245,158,11,0.20),transparent 70%)" }} />

          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div style={{ background: "#0055FF", boxShadow: "0 0 18px rgba(0,85,255,0.5)" }} className="h-9 w-9 rounded-xl flex items-center justify-center">
                  <span className="font-display font-bold text-white text-lg">E</span>
                </div>
                <div className="leading-none">
                  <div className="font-display font-bold text-white tracking-tight">ECO</div>
                  <div className="font-mono-eco text-[9px] uppercase tracking-[0.2em] text-slate-500">Entrepreneur OS</div>
                </div>
              </div>
              <div style={{ borderColor: "rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.1)" }} className="h-14 w-14 rounded-2xl border flex items-center justify-center">
                <Icon name={badgeIcon} size={26} className="text-amber-400" />
              </div>
            </div>

            <div className="mt-10 text-center">
              <div className="font-mono-eco text-[11px] uppercase tracking-[0.3em] text-amber-400">Certificate of Completion</div>
              <p className="text-slate-400 text-sm mt-5">This certifies that</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mt-2 tracking-tight">{name || "Founder"}</h2>
              <p className="text-slate-400 text-sm mt-5">has successfully completed the</p>
              <h3 className="font-display text-xl md:text-2xl font-semibold mt-2" style={{ color: "#4d8bff" }}>{title}</h3>
            </div>

            <div className="mt-9 flex items-center justify-between">
              <div className="text-left">
                <div className="font-mono-eco text-[9px] uppercase tracking-widest text-slate-500">Date</div>
                <div className="text-sm text-slate-200 mt-1">{prettyDate}</div>
              </div>
              {xp != null && (
                <div className="flex items-center gap-1.5 text-amber-400">
                  <Zap size={16} />
                  <span className="font-display font-bold text-white">{xp}</span>
                  <span className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500">XP earned</span>
                </div>
              )}
              <div className="text-right">
                <div className="font-display text-sm text-white italic">{badgeTitle || "ECO Achiever"}</div>
                <div className="font-mono-eco text-[9px] uppercase tracking-widest text-slate-500 mt-1">Badge unlocked</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-5">
          <button onClick={download} disabled={busy} data-testid="certificate-download" className="btn-accent">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <><Download size={16} /> Download certificate</>}
          </button>
        </div>
      </div>
    </div>
  );
}
